import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSession } from '@/contexts/SessionContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  User, MapPin, GraduationCap, Briefcase, CreditCard,
  FileText, CheckCircle, Save, ArrowLeft, ArrowRight, Camera, X, Upload, Loader2, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];
const QUALIFICATIONS = ['PhD', 'M.Phil', 'M.Ed', 'B.Ed', 'M.A', 'M.Sc', 'M.Com', 'B.A', 'B.Sc', 'B.Com', 'D.El.Ed', 'B.Tech', 'M.Tech', 'MBA', 'Other'];
const DESIGNATIONS = ['Principal', 'Vice Principal', 'Head Master', 'PGT', 'TGT', 'PRT', 'NTT', 'PTI', 'Librarian', 'Counselor'];
const DEPARTMENTS = ['Science', 'Arts', 'Commerce', 'Primary', 'Pre-Primary', 'Physical Education', 'Computer'];
const SUBJECTS = ['English', 'Bengali', 'Hindi', 'Mathematics', 'Physics', 'Chemistry', 'Biology', 'History', 'Geography', 'Economics', 'Political Science', 'Computer Science', 'Physical Education', 'Art', 'Music'];

// Indian States
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

// Districts by State
const DISTRICTS_BY_STATE = {
  'West Bengal': ['Kolkata', 'Howrah', 'North 24 Parganas', 'South 24 Parganas', 'Hooghly', 'Nadia', 'Murshidabad', 'Bardhaman', 'Birbhum', 'Bankura', 'Purulia', 'Medinipur East', 'Medinipur West', 'Jalpaiguri', 'Darjeeling', 'Cooch Behar', 'Malda', 'Dinajpur North', 'Dinajpur South'],
  'Bihar': ['Patna', 'Gaya', 'Muzaffarpur', 'Bhagalpur', 'Darbhanga', 'Purnia', 'Nalanda', 'Vaishali', 'Begusarai', 'Samastipur', 'Munger', 'Buxar', 'Katihar', 'Madhubani'],
  'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh', 'Deoghar', 'Giridih', 'Dumka', 'Ramgarh'],
  'Delhi': ['Central Delhi', 'East Delhi', 'New Delhi', 'North Delhi', 'North East Delhi', 'North West Delhi', 'Shahdara', 'South Delhi', 'South East Delhi', 'South West Delhi', 'West Delhi'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Sangli', 'Satara'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Prayagraj', 'Meerut', 'Noida', 'Ghaziabad', 'Gorakhpur', 'Mathura'],
  'Other': ['Other']
};

const TeacherFormPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const { id: teacherId } = useParams(); // For edit mode
  const isEditMode = Boolean(teacherId);
  const { checkSessionRequired } = useSession();
  
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [classes, setClasses] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Personal
    name: '',
    email: '',
    phone: '',
    alternate_phone: '',
    personal_email: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    nationality: 'Indian',
    aadhaar_number: '',
    pan_number: '',
    
    // Address
    present_address: '',
    permanent_address: '',
    city: '',
    district: '',
    state: 'West Bengal',
    pin_code: '',
    same_address: false,
    
    // Qualification
    highest_qualification: '',
    specialization: '',
    university: '',
    passing_year: '',
    professional_qualification: '',
    
    // Experience
    total_experience_years: '',
    previous_school_name: '',
    previous_designation: '',
    
    // Employment
    designation: '',
    department: '',
    subject_specialization: [],
    classes_assigned: [],
    joining_date: new Date().toISOString().split('T')[0],
    employment_type: 'permanent',
    probation_months: '',
    
    // Salary
    basic_salary: '',
    pay_scale: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    pf_number: '',
    esi_number: '',
  });

  useEffect(() => {
    fetchClasses();
    if (isEditMode) {
      fetchTeacherData();
    }
  }, [teacherId]);

  const fetchTeacherData = async () => {
    setFetchingData(true);
    try {
      const res = await axios.get(`${API_URL}/api/teachers/${teacherId}`, { headers: getAuthHeader() });
      const teacher = res.data;
      
      // Map API response to form data
      setFormData({
        name: teacher.name || '',
        email: teacher.email || '',
        phone: teacher.phone || '',
        alternate_phone: teacher.alternate_phone || '',
        personal_email: teacher.personal_email || '',
        date_of_birth: teacher.date_of_birth || '',
        gender: teacher.gender || '',
        blood_group: teacher.blood_group || '',
        nationality: teacher.nationality || 'Indian',
        aadhaar_number: teacher.aadhaar_number || '',
        pan_number: teacher.pan_number || '',
        present_address: teacher.present_address || teacher.address || '',
        permanent_address: teacher.permanent_address || '',
        city: teacher.city || '',
        district: teacher.district || '',
        state: teacher.state || 'West Bengal',
        pin_code: teacher.pin_code || '',
        same_address: teacher.same_address || false,
        highest_qualification: teacher.highest_qualification || teacher.qualification || '',
        specialization: teacher.specialization || '',
        university: teacher.university || '',
        passing_year: teacher.passing_year || '',
        professional_qualification: teacher.professional_qualification || '',
        total_experience_years: teacher.total_experience_years || teacher.experience || '',
        previous_school_name: teacher.previous_school_name || '',
        previous_designation: teacher.previous_designation || '',
        designation: teacher.designation || '',
        department: teacher.department || '',
        subject_specialization: teacher.subject_specialization || teacher.subjects || [],
        classes_assigned: teacher.classes_assigned || [],
        joining_date: teacher.joining_date || '',
        employment_type: teacher.employment_type || 'permanent',
        probation_months: teacher.probation_months || '',
        basic_salary: teacher.basic_salary || teacher.salary || '',
        pay_scale: teacher.pay_scale || '',
        bank_name: teacher.bank_name || '',
        account_number: teacher.account_number || '',
        ifsc_code: teacher.ifsc_code || '',
        pf_number: teacher.pf_number || '',
        esi_number: teacher.esi_number || '',
      });
      
      // Fetch photo from documents
      try {
        const docsRes = await axios.get(`${API_URL}/api/documents/staff/teacher/${teacherId}`, { headers: getAuthHeader() });
        const docs = docsRes.data.documents || [];
        // Get latest photo (sort by uploaded_at descending)
        const photos = docs.filter(d => d.document_type === 'staff_photo')
          .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
        const photoDoc = photos[0];
        if (photoDoc?.cloudinary_url) {
          setPhotoPreview(photoDoc.cloudinary_url);
        } else if (photoDoc?.file_path && photoDoc.file_path.startsWith('http')) {
          setPhotoPreview(photoDoc.file_path);
        }
      } catch (docError) {
        console.error('Failed to fetch photo:', docError);
      }
    } catch (error) {
      console.error('Failed to fetch teacher:', error);
      toast.error('Failed to load teacher data');
    } finally {
      setFetchingData(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/classes`, { headers: getAuthHeader() });
      setClasses(res.data);
    } catch (error) {
      console.error('Failed to fetch classes');
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (100 KB max)
      if (file.size > 100 * 1024) {
        toast.error('Photo size must be less than 100 KB');
        e.target.value = '';
        return;
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        e.target.value = '';
        return;
      }
      
      // Show preview immediately
      setPhotoPreview(URL.createObjectURL(file));
      
      // If editing existing teacher, upload immediately
      if (isEditMode && teacherId) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);
          uploadFormData.append('document_type', 'staff_photo');
          
          await axios.post(
            `${API_URL}/api/documents/staff/upload/teacher/${teacherId}`,
            uploadFormData,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          );
          toast.success('Photo uploaded successfully!');
        } catch (error) {
          toast.error('Photo upload failed');
          setPhotoPreview(null);
        }
      } else {
        // For new teacher, store file to upload after save
        setPhotoFile(file);
      }
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const uploadPhoto = async (teacherId) => {
    if (!photoFile) return;
    
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      formData.append('document_type', 'staff_photo');
      
      await axios.post(
        `${API_URL}/api/documents/staff/upload/teacher/${teacherId}`,
        formData,
        { 
          headers: { 
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Photo uploaded successfully!');
    } catch (error) {
      console.error('Photo upload failed:', error);
      toast.error('Photo upload failed. You can upload later from Teachers page.');
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'same_address' && value) {
        updated.permanent_address = prev.present_address;
      }
      return updated;
    });
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.joining_date) {
      toast.error('Please fill required fields: Name, Email, Phone, Joining Date');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        total_experience_years: formData.total_experience_years ? parseFloat(formData.total_experience_years) : null,
        basic_salary: formData.basic_salary ? parseFloat(formData.basic_salary) : null,
        probation_months: formData.probation_months ? parseInt(formData.probation_months) : null,
      };
      
      let teacherIdResult;
      
      if (isEditMode) {
        // Update existing teacher
        await axios.put(`${API_URL}/api/teachers/${teacherId}`, payload, { headers: getAuthHeader() });
        teacherIdResult = teacherId;
        toast.success('Teacher updated successfully!');
      } else {
        // Create new teacher
        const res = await axios.post(`${API_URL}/api/teachers`, payload, { headers: getAuthHeader() });
        teacherIdResult = res.data.id;
        toast.success(`Teacher added! ID: ${res.data.employee_id}`);
      }
      
      // Upload photo if selected
      if (photoFile && teacherIdResult) {
        await uploadPhoto(teacherIdResult);
      }
      
      navigate('/teachers');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'add'} teacher`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'qualification', label: 'Qualification', icon: GraduationCap },
    { id: 'employment', label: 'Employment', icon: Briefcase },
    { id: 'salary', label: 'Salary & Bank', icon: CreditCard },
  ];

  const currentTabIndex = tabs.findIndex(t => t.id === activeTab);
  const progress = ((currentTabIndex + 1) / tabs.length) * 100;
  
  // Check if session is set
  const sessionCheck = checkSessionRequired();

  if (fetchingData) {
    return (
      <DashboardLayout title="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  // Show session required message for new entries
  if (!isEditMode && !sessionCheck.allowed) {
    return (
      <DashboardLayout title="Add New Teacher">
        <div className="max-w-2xl mx-auto mt-10">
          <Alert variant="destructive">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle>Session Not Set!</AlertTitle>
            <AlertDescription className="mt-2">
              {sessionCheck.message}
            </AlertDescription>
          </Alert>
          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={() => navigate('/teachers')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Teachers
            </Button>
            <Button onClick={() => navigate('/academic')}>
              <GraduationCap className="h-4 w-4 mr-2" /> Go to Academic Settings
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={isEditMode ? "Edit Teacher" : "Add New Teacher"}>
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Back Button */}
        <Button variant="outline" onClick={() => navigate('/teachers')} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Teachers
        </Button>
        
        <div className="flex items-center gap-4">
          <Progress value={progress} className="flex-1" />
          <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              {isEditMode ? 'Edit Teacher' : 'Teacher Registration Form'}
            </CardTitle>
            <CardDescription>{isEditMode ? 'Update teacher details' : 'Fill all required details for teacher registration'}</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-5 mb-6">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger key={tab.id} value={tab.id} className="text-xs">
                      <Icon className="h-4 w-4 mr-1 hidden sm:inline" />
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Personal Information */}
              <TabsContent value="personal" className="space-y-4">
                {/* Photo Upload Section */}
                <div className="flex items-start gap-6 p-4 border rounded-lg bg-muted/30">
                  <div className="flex flex-col items-center">
                    <div className="relative w-32 h-32 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-background">
                      {photoPreview ? (
                        <>
                          <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-1 right-1 h-6 w-6"
                            onClick={removePhoto}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Camera className="h-8 w-8 text-muted-foreground/50" />
                      )}
                    </div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      {photoPreview ? 'Change' : 'Upload Photo'}
                    </Button>
                  </div>
                  <div className="flex-1 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Teacher Photo</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Passport size photo preferred</li>
                      <li>Max size: 100 KB</li>
                      <li>Formats: JPG, PNG, WebP</li>
                      <li>Photo will be used for ID Card</li>
                    </ul>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input value={formData.name} onChange={e => handleInputChange('name', e.target.value)} placeholder="As per documents" />
                  </div>
                  <div>
                    <Label>Official Email *</Label>
                    <Input type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} placeholder="official@school.edu" />
                  </div>
                  <div>
                    <Label>Mobile Number * (10 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.phone} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('phone', val);
                      }}
                      maxLength={10}
                      placeholder="10 digit mobile number"
                    />
                  </div>
                  <div>
                    <Label>Alternate Mobile (10 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.alternate_phone} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('alternate_phone', val);
                      }}
                      maxLength={10}
                      placeholder="10 digit mobile number"
                    />
                  </div>
                  <div>
                    <Label>Personal Email</Label>
                    <Input type="email" value={formData.personal_email} onChange={e => handleInputChange('personal_email', e.target.value)} />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input type="date" value={formData.date_of_birth} onChange={e => handleInputChange('date_of_birth', e.target.value)} />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select value={formData.gender} onValueChange={v => handleInputChange('gender', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Blood Group</Label>
                    <Select value={formData.blood_group} onValueChange={v => handleInputChange('blood_group', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {BLOOD_GROUPS.map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Nationality</Label>
                    <Input value={formData.nationality} onChange={e => handleInputChange('nationality', e.target.value)} />
                  </div>
                  <div>
                    <Label>Aadhaar Number (12 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.aadhaar_number} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                        handleInputChange('aadhaar_number', val);
                      }}
                      maxLength={12}
                      placeholder="12 digit Aadhaar"
                    />
                  </div>
                  <div>
                    <Label>PAN Number</Label>
                    <Input 
                      value={formData.pan_number} 
                      onChange={e => handleInputChange('pan_number', e.target.value.toUpperCase())} 
                      maxLength={10} 
                      placeholder="ABCDE1234F" 
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Address */}
              <TabsContent value="address" className="space-y-4">
                <div>
                  <Label>Present Address</Label>
                  <Textarea value={formData.present_address} onChange={e => handleInputChange('present_address', e.target.value)} rows={2} />
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.same_address} onChange={e => handleInputChange('same_address', e.target.checked)} />
                  <Label className="text-sm">Permanent address same as present</Label>
                </div>
                <div>
                  <Label>Permanent Address</Label>
                  <Textarea value={formData.permanent_address} onChange={e => handleInputChange('permanent_address', e.target.value)} rows={2} disabled={formData.same_address} />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input value={formData.city} onChange={e => handleInputChange('city', e.target.value)} />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select value={formData.state} onValueChange={v => { handleInputChange('state', v); handleInputChange('district', ''); }}>
                      <SelectTrigger><SelectValue placeholder="Select State" /></SelectTrigger>
                      <SelectContent>
                        {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>District</Label>
                    <Select value={formData.district} onValueChange={v => handleInputChange('district', v)} disabled={!formData.state}>
                      <SelectTrigger><SelectValue placeholder={formData.state ? "Select District" : "Select State first"} /></SelectTrigger>
                      <SelectContent>
                        {(DISTRICTS_BY_STATE[formData.state] || DISTRICTS_BY_STATE['Other'] || []).map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>PIN Code (6 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.pin_code} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                        handleInputChange('pin_code', val);
                      }}
                      maxLength={6}
                      placeholder="6 digit PIN"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Qualification */}
              <TabsContent value="qualification" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Highest Qualification</Label>
                    <Select value={formData.highest_qualification} onValueChange={v => handleInputChange('highest_qualification', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {QUALIFICATIONS.map(q => <SelectItem key={q} value={q}>{q}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Specialization</Label>
                    <Input value={formData.specialization} onChange={e => handleInputChange('specialization', e.target.value)} placeholder="e.g., Physics, English" />
                  </div>
                  <div>
                    <Label>University/Board</Label>
                    <Input value={formData.university} onChange={e => handleInputChange('university', e.target.value)} />
                  </div>
                  <div>
                    <Label>Passing Year</Label>
                    <Input value={formData.passing_year} onChange={e => handleInputChange('passing_year', e.target.value)} placeholder="e.g., 2020" />
                  </div>
                  <div>
                    <Label>Professional Qualification (B.Ed/D.El.Ed)</Label>
                    <Input value={formData.professional_qualification} onChange={e => handleInputChange('professional_qualification', e.target.value)} placeholder="B.Ed, M.Ed, D.El.Ed" />
                  </div>
                  <div>
                    <Label>Total Experience (Years)</Label>
                    <Input type="number" value={formData.total_experience_years} onChange={e => handleInputChange('total_experience_years', e.target.value)} />
                  </div>
                  <div>
                    <Label>Previous School Name</Label>
                    <Input value={formData.previous_school_name} onChange={e => handleInputChange('previous_school_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Previous Designation</Label>
                    <Input value={formData.previous_designation} onChange={e => handleInputChange('previous_designation', e.target.value)} />
                  </div>
                </div>
              </TabsContent>

              {/* Employment */}
              <TabsContent value="employment" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Designation *</Label>
                    <Select value={formData.designation} onValueChange={v => handleInputChange('designation', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {DESIGNATIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Select value={formData.department} onValueChange={v => handleInputChange('department', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Joining Date *</Label>
                    <Input type="date" value={formData.joining_date} onChange={e => handleInputChange('joining_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Employment Type</Label>
                    <Select value={formData.employment_type} onValueChange={v => handleInputChange('employment_type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="permanent">Permanent</SelectItem>
                        <SelectItem value="contractual">Contractual</SelectItem>
                        <SelectItem value="part_time">Part Time</SelectItem>
                        <SelectItem value="probation">Probation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.employment_type === 'probation' && (
                    <div>
                      <Label>Probation Period (Months)</Label>
                      <Input type="number" value={formData.probation_months} onChange={e => handleInputChange('probation_months', e.target.value)} />
                    </div>
                  )}
                </div>

                <div className="p-4 border rounded-lg space-y-3">
                  <Label>Subject Specialization (Select multiple)</Label>
                  <div className="flex flex-wrap gap-2">
                    {SUBJECTS.map(sub => (
                      <Badge
                        key={sub}
                        variant={formData.subject_specialization.includes(sub) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => handleMultiSelect('subject_specialization', sub)}
                      >
                        {sub}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="p-4 border rounded-lg space-y-3">
                  <Label>Classes Assigned (Select multiple)</Label>
                  <div className="flex flex-wrap gap-2">
                    {classes.map(cls => (
                      <Badge
                        key={cls.id}
                        variant={formData.classes_assigned.includes(cls.id) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => handleMultiSelect('classes_assigned', cls.id)}
                      >
                        {cls.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* Salary & Bank */}
              <TabsContent value="salary" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Basic Salary (₹)</Label>
                    <Input type="number" value={formData.basic_salary} onChange={e => handleInputChange('basic_salary', e.target.value)} />
                  </div>
                  <div>
                    <Label>Pay Scale/Grade</Label>
                    <Input value={formData.pay_scale} onChange={e => handleInputChange('pay_scale', e.target.value)} placeholder="e.g., Level-7" />
                  </div>
                  <div>
                    <Label>Bank Name</Label>
                    <Input value={formData.bank_name} onChange={e => handleInputChange('bank_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Account Number</Label>
                    <Input value={formData.account_number} onChange={e => handleInputChange('account_number', e.target.value)} />
                  </div>
                  <div>
                    <Label>IFSC Code</Label>
                    <Input value={formData.ifsc_code} onChange={e => handleInputChange('ifsc_code', e.target.value)} placeholder="e.g., SBIN0001234" />
                  </div>
                  <div>
                    <Label>PF Number</Label>
                    <Input value={formData.pf_number} onChange={e => handleInputChange('pf_number', e.target.value)} />
                  </div>
                  <div>
                    <Label>ESI Number</Label>
                    <Input value={formData.esi_number} onChange={e => handleInputChange('esi_number', e.target.value)} />
                  </div>
                </div>

                <div className="flex justify-center pt-6">
                  <Button size="lg" onClick={handleSubmit} disabled={loading}>
                    {loading ? 'Saving...' : <><CheckCircle className="h-4 w-4 mr-2" /> {isEditMode ? 'Update Teacher' : 'Save Teacher'}</>}
                  </Button>
                </div>
              </TabsContent>
            </Tabs>

            {/* Navigation */}
            <div className="flex justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  const idx = tabs.findIndex(t => t.id === activeTab);
                  if (idx > 0) setActiveTab(tabs[idx - 1].id);
                }}
                disabled={activeTab === 'personal'}
              >
                <ArrowLeft className="h-4 w-4 mr-2" /> Previous
              </Button>
              <Button
                onClick={() => {
                  const idx = tabs.findIndex(t => t.id === activeTab);
                  if (idx < tabs.length - 1) setActiveTab(tabs[idx + 1].id);
                }}
                disabled={activeTab === 'salary'}
              >
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TeacherFormPage;
